import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { AuthService } from '@app/core/auth.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ChartUtilities } from '@app/shared/utils/chartData';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { DateUtils } from '@app/shared/utils/date.utils';
import { ErrorMessages } from '@app/shared/constants/constants';
import { MonthsEnum } from '@app/shared/constants/global.enum';
import { LOCATION_OPTIONS } from '../../my-request/my-request-form/my-request-form.constants';
import {
  AssetManagementService,
  CardType,
} from '../../asset-management-service';

type damageItem = {
  osType: string;
  number: number;
  [key: string]: any;
  totalCost: number;
};
@Component({
  selector: 'app-damage-analytics-card',
  templateUrl: './damage-analytics-card.component.html',
  styleUrl: './damage-analytics-card.component.scss',
})
export class DamageAnalyticsCardComponent implements OnInit {
  isLoading: boolean = false;
  chartData: any[] = [];
  errorMessage: string | null = null;
  isChartReady: boolean = false;
  damageAnalyticsData: any[] = [];

  imageSrc: string = 'assets/img/no-data-found.png';
  chartType = 'doughnut' as const;
  chartLabelKey: string = 'label';
  chartValueKey: string = 'value';
  locationOptions: any[] = LOCATION_OPTIONS;
  monthOptions: any[] = [];
  selectedMonthValue: string = '';
  legendItems: {
    label: string;
    value: number;
    color: string;
    totalCost: number;
  }[] = [];
  isDataLoaded: boolean = true;
  selectedLocation: string = '';
  selectedMonth: string = '';
  monthSelected: any = null;
  yearSelected: any = null;
  errorMsg: string = ErrorMessages.NoDataAvailable;

  constructor(
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly assetManagementService: AssetManagementService,
    private readonly route: ActivatedRoute,
    private readonly authService: AuthService,
  ) {
    this.monthOptions = DateUtils.generateMonthOptions();
    if (this.monthOptions.length > 0) {
      this.selectedMonthValue = this.monthOptions[0].value;
    }
  }

  ngOnInit(): void {
    const currentDate = new Date();
    this.monthSelected = DateUtils.getMonthName(currentDate.getMonth());
    this.yearSelected = currentDate.getFullYear().toString();
    const location = this.assetManagementService.getUserLocationFromSession();
    const foundLocation = this.locationOptions.find(
      (opt) => opt.value === location,
    );
    this.selectedLocation = foundLocation ? foundLocation?.value : 'All';
    this.fetchDamageAnalyticsData(this.selectedLocation, this.monthSelected);
  }

  navigateToMyRequests(cardType: CardType = 'header'): void {
    localStorage.setItem('selectedCard', cardType);
    this.router.navigate(['damage-analytics'], {
      relativeTo: this.route,
      state: { 
        selectedMonth: this.selectedMonthValue,
        selectedLocation: this.selectedLocation 
      },
    });
  }

  navigateToDamageAnalytics() {
    this.navigateToMyRequests('damage-analytics');
  }

  private updateChartData(data: damageItem[]): void {
    this.isChartReady = false;

    if (data.length === 0) {
      this.chartData = [];
      this.legendItems = [];
      this.cdr.detectChanges();
      return;
    }

    const palette = ChartUtilities.getChartColors();

    const damageItem = data.map((d, i) => {
      const color = palette[i % palette.length];
      return {
        label: d.osType,
        value: d.number,
        color: color,
        totalCost: d.totalCost,
      };
    });
    
    this.legendItems = damageItem;
    this.chartData = damageItem;
    this.isChartReady = true;
    this.cdr.detectChanges();
  }

  private resetData(): void {
    this.damageAnalyticsData = [];
    this.chartData = [];
    this.isChartReady = false;
  }

  onLocationChange(event: any) {
    if (event && typeof event === 'object') {
      this.selectedLocation = event.label;
    } else {
      this.selectedLocation = event;
    }
    this.fetchDamageAnalyticsData(this.selectedLocation, this.monthSelected);
  }

  handleMonthChange(selectedMonth: any) {
    this.selectedMonthValue = selectedMonth;
    const [month, year] = selectedMonth.split(',').map((s: string) => s.trim());
    this.monthSelected = DateUtils.getMonthName(Number(month) - 1);
    this.yearSelected = year;
    this.fetchDamageAnalyticsData(this.selectedLocation, this.monthSelected);
  }

  private fetchDamageAnalyticsData(
    location: string,
    month: string | number,
  ): void {
    this.isLoading = true;
    let monthStr: string;
    if (typeof month === 'string') {
      const monthNames = Object.keys(MonthsEnum);
      const monthName = month.split(',')[0].trim();
      const monthNum = monthNames.indexOf(monthName) + 1;
      monthStr = monthNum < 10 ? `0${monthNum}` : `${monthNum}`;
    } else {
      monthStr = month.toString().padStart(2, '0');
    }
    this.assetManagementService
      .fetchDamageAnalytics(location, monthStr)
      .subscribe({
        next: (data) => {
          let analyticsData = Array.isArray(data)
            ? data.map((item: any) => ({
                osType: item.osType || 'MacBook',
                number: item.number || 0,
                totalCost: item.totalCost || 0,
              }))
            : [];
          this.damageAnalyticsData = analyticsData;
          this.updateChartData(this.damageAnalyticsData);
          this.errorMessage = null;
          this.isLoading = false;
        },
        error: () => {
          this.resetData();
          this.isLoading = false;
        },
      });
  }

  formatCurrency(amount: number): string {
    if (amount === null || amount === undefined || isNaN(amount)) {
      return '₹0';
    }
    return '₹' + amount.toLocaleString('en-IN');
  }
}
